import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

import { useCreateCommunityFeedItemComment } from 'src/Hooks/Mutations/useCreateFeedItemComment';
import { useDeleteCommunityFeedItemComment } from 'src/Hooks/Mutations/useDeleteFeedItemComment';
import { useEditCommunityFeedItemComment } from 'src/Hooks/Mutations/useEditFeedItemComment';
import useFetchCommunityFeedItemComments from 'src/Hooks/Queries/useFetchCommunityFeedItemComments';
import { CommunityFeedItemDto } from 'src/Services/API/generated';

import { CommentsList } from '../CommentsList';

export type FeedItemCommentsProps = {
  feedItem: CommunityFeedItemDto;
  grow?: boolean;
  highlightedCommentId?: string;
};

const FeedItemComments = ({ feedItem, grow, highlightedCommentId }: FeedItemCommentsProps) => {
  const { t } = useTranslation();

  const {
    data: infiniteComments,
    isFetchingNextPage,
    isLoading,
  } = useFetchCommunityFeedItemComments(feedItem.id, feedItem.comments.items);

  const { mutate: createComment, isLoading: isCreateLoading } = useCreateCommunityFeedItemComment({
    onSuccess() {
      notifications.show({
        message: t('community.comments.createSuccess', 'Votre commentaire a été publié.'),
      });
    },
  });

  const { mutate: editComment, isLoading: isEditLoading } = useEditCommunityFeedItemComment({
    onSuccess() {
      notifications.show({
        message: t('community.comments.edited', 'Votre commentaire est modifié.'),
      });
    },
  });

  const { mutate: deleteComment } = useDeleteCommunityFeedItemComment({
    onSuccess() {
      notifications.show({
        message: t('community.comments.deleted', 'Votre commentaire est supprimé.'),
      });
    },
  });

  const comments = infiniteComments?.pages.flatMap(page => page.items) ?? [];

  const handleCreateComment = (message: string) => {
    createComment({ feedItemId: feedItem.id, message });
  };

  const handleEditComment = (commentId: string, message: string) => {
    editComment({ feedItemId: feedItem.id, commentId, message });
  };

  const handleDeleteComment = (commentId: string) => {
    deleteComment({ feedItemId: feedItem.id, commentId });
  };

  return (
    <CommentsList
      comments={comments}
      isLoading={isLoading}
      isFetchingMore={isFetchingNextPage}
      highlightedCommentId={highlightedCommentId}
      onCreateComment={handleCreateComment}
      onEditComment={handleEditComment}
      onDeleteComment={handleDeleteComment}
      isCreateLoading={isCreateLoading}
      isEditLoading={isEditLoading}
      grow={grow}
      threaded
      createPlaceholder={t('community.replyTo', {
        name: feedItem.author.firstName,
        defaultValue: 'Répondre à {{name}}…',
      })}
    />
  );
};

export default FeedItemComments;
